import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { apiGet, apiPatch, apiPost, apiPut, apiDelete } from "../api/http";
import { useAuth } from "../context/AuthContext";
import { useActiveSite } from "../context/ActiveSiteContext";
import { useToast } from "../context/ToastContext";
import { parseApiError } from "../utils/apiErrors";
import ChangeEmailModal from "../components/ChangeEmailModal";
import { formatDateTime } from "../utils/dateTime";

const TABS = [
  { id: "profile", label: "Profile" },
  { id: "workspace", label: "Workspace Settings" },
  { id: "members", label: "Workspace Members" },
];

function formatLimit(value) {
  if (value >= 999999) return "Unlimited";
  return String(value);
}

export default function Settings() {
  const { user, refreshUser, logout } = useAuth();
  const { showError, showSuccess } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [scheduleCompletionEmail, setScheduleCompletionEmail] = useState(true);
  const [auditCompletionEmail, setAuditCompletionEmail] = useState(false);
  const [showChangeEmail, setShowChangeEmail] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [copied, setCopied] = useState(false);

  const [transferTargetId, setTransferTargetId] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [showDeleteWsModal, setShowDeleteWsModal] = useState(false);
  const [confirmWsName, setConfirmWsName] = useState("");
  const [deletingWs, setDeletingWs] = useState(false);

  const { activeWorkspace, refreshSites } = useActiveSite();
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState("member");
  const [addingMember, setAddingMember] = useState(false);
  
  const [wsName, setWsName] = useState("");
  const [wsLogo, setWsLogo] = useState("");
  const [wsColor, setWsColor] = useState("");
  const [savingWsName, setSavingWsName] = useState(false);
  const [savingWsBranding, setSavingWsBranding] = useState(false);

  useEffect(() => {
    if (activeWorkspace) {
      setWsName(activeWorkspace.name || "");
      setWsLogo(activeWorkspace.brand_logo || "");
      setWsColor(activeWorkspace.brand_color || "");
      
      setLoadingMembers(true);
      apiGet(`/api/workspaces/${activeWorkspace.id}/members`)
        .then((res) => {
          setMembers(Array.isArray(res.data) ? res.data : []);
        })
        .catch((err) => {
          console.error("Failed to load workspace members", err);
        })
        .finally(() => {
          setLoadingMembers(false);
        });
    }
  }, [activeWorkspace]);

  const handleSaveWorkspaceName = async (e) => {
    e.preventDefault();
    if (!activeWorkspace || !wsName.trim()) return;
    setSavingWsName(true);
    try {
      await apiPut(`/api/workspaces/${activeWorkspace.id}/name`, {
        name: wsName.trim(),
      });
      showSuccess("Workspace name updated successfully.");
      await refreshSites();
    } catch (err) {
      showError(parseApiError(err, "Failed to update workspace name"));
    } finally {
      setSavingWsName(false);
    }
  };

  const handleSaveWorkspaceBranding = async (e) => {
    e.preventDefault();
    if (!activeWorkspace) return;
    setSavingWsBranding(true);
    try {
      await apiPut(`/api/workspaces/${activeWorkspace.id}/branding`, {
        brandLogo: wsLogo.trim() || null,
        brandColor: wsColor.trim() || null,
      });
      showSuccess("Workspace branding updated successfully.");
      await refreshSites();
    } catch (err) {
      showError(parseApiError(err, "Failed to update workspace branding"));
    } finally {
      setSavingWsBranding(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!activeWorkspace || !newMemberEmail.trim()) return;
    setAddingMember(true);
    try {
      await apiPost(`/api/workspaces/${activeWorkspace.id}/members`, {
        email: newMemberEmail.trim(),
        role: newMemberRole,
      });
      showSuccess(`Successfully added member ${newMemberEmail}.`);
      setNewMemberEmail("");
      
      const res = await apiGet(`/api/workspaces/${activeWorkspace.id}/members`);
      setMembers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showError(parseApiError(err, "Failed to add member"));
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!activeWorkspace) return;
    if (!window.confirm("Are you sure you want to remove this member?")) return;
    try {
      await apiDelete(`/api/workspaces/${activeWorkspace.id}/members/${userId}`);
      showSuccess("Member removed successfully.");
      setMembers((curr) => curr.filter((m) => m.id !== userId));
    } catch (err) {
      showError(parseApiError(err, "Failed to remove member"));
    }
  };

  const handleTransferOwnership = async (targetUserId) => {
    if (!activeWorkspace || !targetUserId) return;
    setTransferring(true);
    try {
      await apiPut(`/api/workspaces/${activeWorkspace.id}/members/${targetUserId}`, {
        role: "owner",
      });
      showSuccess("Workspace ownership transferred successfully!");
      setTransferTargetId("");
      await loadProfile();
      await refreshUser();
      const res = await apiGet(`/api/workspaces/${activeWorkspace.id}/members`);
      setMembers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showError(parseApiError(err, "Failed to transfer workspace ownership"));
    } finally {
      setTransferring(false);
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!activeWorkspace) return;
    if (confirmWsName.trim() !== activeWorkspace.name) {
      showError("Confirmation name does not match workspace name.");
      return;
    }
    setDeletingWs(true);
    try {
      await apiDelete(`/api/workspaces/${activeWorkspace.id}`);
      showSuccess("Workspace deleted successfully.");
      await refreshSites();
      window.location.href = "/";
    } catch (err) {
      showError(parseApiError(err, "Failed to delete workspace"));
    } finally {
      setDeletingWs(false);
      setShowDeleteWsModal(false);
      setConfirmWsName("");
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmEmail.trim().toLowerCase() !== (resolvedProfile?.email || email || "").toLowerCase()) {
      showError("Confirmation email does not match your account email.");
      return;
    }
    setIsDeleting(true);
    try {
      await apiPost("/api/auth/delete-account", { email: confirmEmail });
      showSuccess("Your account was successfully deleted.");
      await logout();
      navigate("/login?mode=signin", { replace: true });
    } catch (err) {
      showError(parseApiError(err, "Failed to delete account"));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const activeTab = useMemo(() => {
    const raw = searchParams.get("tab");
    if (raw === "members") return "members";
    if (raw === "workspace" || raw === "settings") return "workspace";
    return "profile";
  }, [searchParams]);

  const resolvedProfile = profileData || user || {};
  const createdAtValue = resolvedProfile?.created_at || resolvedProfile?.createdAt;
  const planName = String(resolvedProfile?.plan_name || "free").toLowerCase();
  const planStatus = String(resolvedProfile?.plan_status || "active").toLowerCase();
  const isPremium = planName === "solo" || planName === "agency" || planName === "premium";
  const usage = resolvedProfile?.usage || {};
  const planFeatures = resolvedProfile?.plan_features || {};
  const planComparison = resolvedProfile?.plan_comparison || {};

  const isOwner = useMemo(() => {
    return activeWorkspace?.role === "owner" || activeWorkspace?.owner_id === user?.id;
  }, [activeWorkspace, user]);

  const loadProfile = () => {
    setLoadingProfile(true);
    return apiGet("/api/auth/profile")
      .then((meRes) => {
        setProfileData(meRes?.data?.user || null);
      })
      .catch((err) => {
        showError(parseApiError(err, "Failed to load profile"));
      })
      .finally(() => {
        setLoadingProfile(false);
      });
  };

  useEffect(() => {
    let mounted = true;
    setLoadingProfile(true);
    Promise.all([
      apiGet("/api/auth/profile"),
      apiGet("/api/settings/notifications"),
    ])
      .then(([meRes, settingsRes]) => {
        if (!mounted) return;
        setProfileData(meRes?.data?.user || null);
        const data = settingsRes?.data || {};
        setEmail(data.email || "");
        setScheduleCompletionEmail(Boolean(data.preferences?.scheduleCompletionEmail));
        setAuditCompletionEmail(Boolean(data.preferences?.auditCompletionEmail));
      })
      .catch((err) => {
        if (!mounted) return;
        showError(parseApiError(err, "Failed to load profile/settings"));
      })
      .finally(() => {
        if (!mounted) return;
        setLoadingProfile(false);
      });
    return () => {
      mounted = false;
    };
  }, [showError]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [activeTab]);

  const saveNotificationPreferences = async () => {
    setSaving(true);
    try {
      await apiPatch("/api/settings/notifications", {
        scheduleCompletionEmail,
        auditCompletionEmail,
      });
      showSuccess("Notification preferences saved successfully.");
    } catch (err) {
      showError(parseApiError(err, "Failed to save notification preferences"));
    } finally {
      setSaving(false);
    }
  };

  const handleEmailChanged = async (updatedUser) => {
    if (updatedUser) {
      setProfileData(updatedUser);
      setEmail(updatedUser.email || "");
    } else {
      await loadProfile();
      await refreshUser();
    }
    showSuccess("Email updated successfully.");
  };

  const initials = String(resolvedProfile?.name || resolvedProfile?.email || "U")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "U";

  return (
    <div className="page">
      <h1 className="page-title">Profile & Settings</h1>
      <p className="dashboard-lead" style={{ marginTop: "-8px" }}>
        Manage your user details, workspace settings, plan features, and team access.
      </p>

      <div className="settings-layout">
        <aside className="settings-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`settings-tab-btn ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setSearchParams(tab.id === "profile" ? {} : { tab: tab.id })}
            >
              {tab.label}
            </button>
          ))}
        </aside>

        <section className="card settings-content-card">
          {activeTab === "profile" ? (
            loadingProfile ? (
              <div className="skeleton-table">
                <div className="skeleton-block skeleton-row" />
                <div className="skeleton-block skeleton-row" />
                <div className="skeleton-block skeleton-row" />
              </div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "32px", alignItems: "start" }}>
                  <div>
                    <div className="settings-hero">
                      <div className="settings-avatar">{initials}</div>
                      <div>
                        <h2 className="page-subtitle" style={{ margin: 0 }}>{resolvedProfile?.name || "Unknown user"}</h2>
                        <div style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>{resolvedProfile?.email || "Unavailable"}</div>
                      </div>
                    </div>

                    <div className="settings-kv">
                      <div className="settings-kv-label">Name</div>
                      <div>{resolvedProfile?.name || "Unknown user"}</div>

                      <div className="settings-kv-label">Email</div>
                      <div className="settings-email-row">
                        <span>{resolvedProfile?.email || "Unavailable"}</span>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowChangeEmail(true)}>
                          Change email
                        </button>
                      </div>

                      {resolvedProfile?.pending_email ? (
                        <>
                          <div className="settings-kv-label">Pending email</div>
                          <div className="pending-email-note">
                            {resolvedProfile.pending_email}
                            <span> — awaiting verification</span>
                            <button type="button" className="auth-link-btn" onClick={() => setShowChangeEmail(true)}>
                              Complete verification
                            </button>
                          </div>
                        </>
                      ) : null}

                      <div className="settings-kv-label">User Id</div>
                      <div className="settings-user-id">{resolvedProfile?.user_id || resolvedProfile?.id || "Unavailable"}</div>

                      <div className="settings-kv-label">Joined since</div>
                      <div>{createdAtValue ? formatDateTime(createdAtValue) : "Unavailable"}</div>
                    </div>

                    {/* Notifications Preferences */}
                    <div className="settings-section" style={{ marginTop: 24, paddingTop: 18, borderTop: "1px solid var(--border)" }}>
                      <h3 className="section-title" style={{ fontSize: "1rem", marginBottom: 8 }}>Notifications</h3>
                      <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
                        <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          <input
                            type="checkbox"
                            checked={scheduleCompletionEmail}
                            onChange={(e) => setScheduleCompletionEmail(e.target.checked)}
                          />
                          <span>Email me when scheduled audits complete</span>
                        </label>
                        <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          <input
                            type="checkbox"
                            checked={auditCompletionEmail}
                            onChange={(e) => setAuditCompletionEmail(e.target.checked)}
                          />
                          <span>Email me when manual deep audits complete</span>
                        </label>
                      </div>
                      <button type="button" className="btn btn-primary" disabled={saving} onClick={saveNotificationPreferences}>
                        {saving ? "Saving..." : "Save Preferences"}
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="usage-summary" style={{ margin: "0" }}>
                      <div>
                        <strong>{usage.audits ?? 0}</strong>
                        <span>Audits</span>
                      </div>
                      <div>
                        <strong>{usage.projects ?? 0}</strong>
                        <span>Projects</span>
                      </div>
                      <div>
                        <strong>{usage.schedules ?? 0}</strong>
                        <span>Schedules</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="settings-section danger-zone" style={{ marginTop: 40, paddingTop: 24, borderTop: "2px dashed var(--danger)" }}>
                  <h3 className="section-title" style={{ fontSize: "1.1rem", color: "var(--danger)", marginBottom: 8 }}>
                    Danger zone
                  </h3>
                  {isOwner && members.length > 1 ? (
                    <div>
                      <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: 12 }}>
                        You are the owner of the active workspace <strong>{activeWorkspace?.name}</strong>. Before deleting your account, you must transfer ownership to another team member.
                      </p>
                      <div style={{ display: "flex", gap: 10, alignItems: "end", maxWidth: "480px" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                          <label style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--text-muted)" }}>Select New Owner</label>
                          <select
                            className="input"
                            value={transferTargetId}
                            onChange={(e) => setTransferTargetId(e.target.value)}
                            style={{ height: "40px", boxSizing: "border-box" }}
                          >
                            <option value="">-- Choose team member --</option>
                            {members.filter((m) => m.id !== user?.id).map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.name || m.email} ({m.email})
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          className="btn"
                          disabled={!transferTargetId || transferring}
                          onClick={() => handleTransferOwnership(transferTargetId)}
                          style={{
                            height: "40px",
                            padding: "0 16px",
                            boxSizing: "border-box",
                            background: "var(--accent)",
                            color: "#fff",
                            opacity: (!transferTargetId || transferring) ? 0.6 : 1
                          }}
                        >
                          {transferring ? "Transferring..." : "Transfer Ownership"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: 12 }}>
                        Permanently delete your account and all associated audit histories, schedules, and projects. This action cannot be undone.
                      </p>
                      <button type="button" className="btn" style={{ background: "var(--danger)", color: "#fff" }} onClick={() => setShowDeleteModal(true)}>
                        Delete account
                      </button>
                    </div>
                  )}
                </div>
              </>
            )
          ) : activeTab === "workspace" ? (
            <div>
              <h2 className="page-subtitle" style={{ marginTop: 0 }}>Workspace Settings</h2>
              
              {!activeWorkspace ? (
                <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>
                  Please select a workspace from the top navigation bar to manage its settings.
                </p>
              ) : (
                <div>
                  {/* Workspace Details Form */}
                  <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid var(--border)" }}>
                    <h3 className="section-title" style={{ fontSize: "1.1rem", marginBottom: 6 }}>
                      Workspace Details
                    </h3>
                    <form onSubmit={handleSaveWorkspaceName} style={{ display: "grid", gap: 12 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ fontSize: "0.82rem", fontWeight: 600 }}>Workspace Name</label>
                        <input
                          className="input"
                          type="text"
                          value={wsName}
                          onChange={(e) => setWsName(e.target.value)}
                          placeholder="Workspace Name"
                          required
                        />
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <button type="submit" className="btn btn-primary" disabled={savingWsName}>
                          {savingWsName ? "Saving Name..." : "Save Workspace Name"}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* White-label PDF Branding Form */}
                  <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid var(--border)" }}>
                    <h3 className="section-title" style={{ fontSize: "1.1rem", marginBottom: 6 }}>
                      White-label PDF Branding
                    </h3>
                    <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: 12 }}>
                      Customize the logo and theme color of your generated PDF audit reports.
                    </p>
                    <form onSubmit={handleSaveWorkspaceBranding} style={{ display: "grid", gap: 12, opacity: isPremium ? 1 : 0.6 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ fontSize: "0.82rem", fontWeight: 600 }}>Brand Logo URL</label>
                        <input
                          className="input"
                          type="url"
                          value={wsLogo}
                          onChange={(e) => setWsLogo(e.target.value)}
                          placeholder="https://example.com/logo.png"
                          disabled={!isPremium}
                        />
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ fontSize: "0.82rem", fontWeight: 600 }}>Accent Color (Hex)</label>
                        <div style={{ display: "flex", gap: 8 }}>
                          <input
                            className="input"
                            type="color"
                            value={wsColor || "#3b82f6"}
                            onChange={(e) => setWsColor(e.target.value)}
                            disabled={!isPremium}
                            style={{ width: 44, height: 38, padding: 2, cursor: isPremium ? "pointer" : "default" }}
                          />
                          <input
                            className="input"
                            type="text"
                            placeholder="#3b82f6"
                            value={wsColor}
                            onChange={(e) => setWsColor(e.target.value)}
                            disabled={!isPremium}
                            style={{ flex: 1 }}
                          />
                        </div>
                      </div>

                      {!isPremium && (
                        <div className="premium-lock-note" style={{ margin: "4px 0" }}>
                          <span>White-label reports is a Premium feature. Upgrade your plan to customize PDF reports.</span>
                        </div>
                      )}

                      <div style={{ marginTop: 8 }}>
                        <button type="submit" className="btn btn-primary" disabled={savingWsBranding || !isPremium}>
                          {savingWsBranding ? "Saving Branding..." : "Save Branding"}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Subscription Plan & Usage Details */}
                  <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid var(--border)" }}>
                    <h3 className="section-title" style={{ fontSize: "1.1rem", marginBottom: 12 }}>
                      Subscription Plan & Usage
                    </h3>
                    
                    <div className={`plan-status-card ${isPremium ? "premium" : "free"}`} style={{ marginTop: 0 }}>
                      <div style={{ fontWeight: 700, marginBottom: 4, textTransform: "capitalize" }}>
                        Current Plan: {activeWorkspace?.plan_name || "free"}
                      </div>
                      <div style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: 8 }}>
                        Status: {activeWorkspace?.pending_upgrade_status === "pending" ? "pending upgrade review" : "active"}
                      </div>
                      <div className="plan-status-meta" style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <div>Manual Audits: <strong>{usage.manual_audits_used ?? 0}</strong> / {formatLimit(planFeatures.manual_audits_per_month)} this month</div>
                        <div>Monitored Sites: <strong>{usage.monitored_sites ?? 0}</strong> / {formatLimit(planFeatures.monitored_sites)} enabled</div>
                        <div>Projects: <strong>{usage.projects ?? 0}</strong> / {formatLimit(planFeatures.max_projects)} active</div>
                      </div>
                      <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
                        <Link to="/pricing" className="btn btn-primary">
                          {activeWorkspace?.pending_upgrade_status === "pending" ? "View Pricing" : "Upgrade Plan"}
                        </Link>
                        {activeWorkspace?.pending_upgrade_status === "pending" ? (
                          <span style={{ fontSize: "0.85rem", color: "var(--warning)" }}>
                            ⏳ Upgrade request is pending review
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="usage-summary" style={{ margin: "16px 0 0 0" }}>
                      <div>
                        <strong>{usage.audits ?? 0}</strong>
                        <span>Audits</span>
                      </div>
                      <div>
                        <strong>{usage.projects ?? 0}</strong>
                        <span>Projects</span>
                      </div>
                      <div>
                        <strong>{usage.schedules ?? 0}</strong>
                        <span>Schedules</span>
                      </div>
                    </div>
                  </div>

                  {/* Plan Comparison Table */}
                  <div style={{ marginTop: 20 }}>
                    <h3 className="section-title" style={{ fontSize: "1.1rem", marginBottom: 6 }}>
                      Plan Comparison
                    </h3>
                    <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: 12 }}>
                      See what your current plan includes compared to Solo and Agency tiers.
                    </p>
                    <div className="plan-compare-table">
                      <div className="plan-compare-row header" style={{ gridTemplateColumns: "1.4fr 0.8fr 0.8fr 0.8fr" }}>
                        <div className="plan-compare-feature">Feature</div>
                        <div className="plan-compare-cell">Free</div>
                        <div className="plan-compare-cell">Solo</div>
                        <div className="plan-compare-cell">Agency</div>
                      </div>
                      <div className="plan-compare-row" style={{ gridTemplateColumns: "1.4fr 0.8fr 0.8fr 0.8fr" }}>
                        <div className="plan-compare-feature">Manual audits / mo</div>
                        <div className="plan-compare-cell">{formatLimit(planComparison.free?.manual_audits_per_month)}</div>
                        <div className="plan-compare-cell" style={{ color: "#1d4ed8", fontWeight: 600 }}>{formatLimit(planComparison.solo?.manual_audits_per_month)}</div>
                        <div className="plan-compare-cell" style={{ color: "#1d4ed8", fontWeight: 600 }}>{formatLimit(planComparison.agency?.manual_audits_per_month)}</div>
                      </div>
                      <div className="plan-compare-row" style={{ gridTemplateColumns: "1.4fr 0.8fr 0.8fr 0.8fr" }}>
                        <div className="plan-compare-feature">Monitored sites</div>
                        <div className="plan-compare-cell">{formatLimit(planComparison.free?.monitored_sites)}</div>
                        <div className="plan-compare-cell" style={{ color: "#1d4ed8", fontWeight: 600 }}>{formatLimit(planComparison.solo?.monitored_sites)}</div>
                        <div className="plan-compare-cell" style={{ color: "#1d4ed8", fontWeight: 600 }}>{formatLimit(planComparison.agency?.monitored_sites)}</div>
                      </div>
                      <div className="plan-compare-row" style={{ gridTemplateColumns: "1.4fr 0.8fr 0.8fr 0.8fr" }}>
                        <div className="plan-compare-feature">Active projects</div>
                        <div className="plan-compare-cell">{formatLimit(planComparison.free?.max_projects)}</div>
                        <div className="plan-compare-cell" style={{ color: "#1d4ed8", fontWeight: 600 }}>{formatLimit(planComparison.solo?.max_projects)}</div>
                        <div className="plan-compare-cell" style={{ color: "#1d4ed8", fontWeight: 600 }}>{formatLimit(planComparison.agency?.max_projects)}</div>
                      </div>
                      <div className="plan-compare-row" style={{ gridTemplateColumns: "1.4fr 0.8fr 0.8fr 0.8fr" }}>
                        <div className="plan-compare-feature">PDF export</div>
                        <div className="plan-compare-cell">{planComparison.free?.export_pdf ? "Yes" : "No"}</div>
                        <div className="plan-compare-cell" style={{ color: "#1d4ed8", fontWeight: 600 }}>{planComparison.solo?.export_pdf ? "Yes" : "No"}</div>
                        <div className="plan-compare-cell" style={{ color: "#1d4ed8", fontWeight: 600 }}>{planComparison.agency?.export_pdf ? "Yes" : "No"}</div>
                      </div>
                      <div className="plan-compare-row" style={{ gridTemplateColumns: "1.4fr 0.8fr 0.8fr 0.8fr" }}>
                        <div className="plan-compare-feature">Site crawl</div>
                        <div className="plan-compare-cell">{planComparison.free?.site_crawl ? "Yes" : "No"}</div>
                        <div className="plan-compare-cell" style={{ color: "#1d4ed8", fontWeight: 600 }}>{planComparison.solo?.site_crawl ? "Yes" : "No"}</div>
                        <div className="plan-compare-cell" style={{ color: "#1d4ed8", fontWeight: 600 }}>{planComparison.agency?.site_crawl ? "Yes" : "No"}</div>
                      </div>
                    </div>
                  </div>

                  {/* Danger Zone */}
                  <div className="settings-section danger-zone" style={{ marginTop: 40, paddingTop: 24, borderTop: "2px dashed var(--danger)" }}>
                    <h3 className="section-title" style={{ fontSize: "1.1rem", color: "var(--danger)", marginBottom: 8 }}>
                      Danger zone
                    </h3>
                    {isOwner ? (
                      <div>
                        {/* Always allow deletion of the workspace */}
                        <div style={{ marginBottom: members.length > 1 ? 24 : 0 }}>
                          <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: 12 }}>
                            Permanently delete this workspace and all associated projects, schedules, crawling logs, and audit reports for all team members. This action cannot be undone.
                          </p>
                          <button
                            type="button"
                            className="btn"
                            style={{ background: "var(--danger)", color: "#fff" }}
                            onClick={() => setShowDeleteWsModal(true)}
                          >
                            Delete Workspace
                          </button>
                        </div>

                        {/* If there are other members, also offer transfer ownership option */}
                        {members.length > 1 && (
                          <div style={{ paddingTop: 20, borderTop: "1px solid var(--border)", marginTop: 20 }}>
                            <h4 style={{ fontSize: "0.95rem", color: "var(--text-heading)", marginBottom: 6 }}>
                              Transfer Workspace Ownership
                            </h4>
                            <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: 12 }}>
                              Alternatively, if you want to leave the workspace, you can transfer ownership to another team member.
                            </p>
                            <div style={{ display: "flex", gap: 10, alignItems: "end", maxWidth: "480px" }}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                                <label style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--text-muted)" }}>Select New Owner</label>
                                <select
                                  className="input"
                                  value={transferTargetId}
                                  onChange={(e) => setTransferTargetId(e.target.value)}
                                  style={{ height: "40px", boxSizing: "border-box" }}
                                >
                                  <option value="">-- Choose team member --</option>
                                  {members.filter((m) => m.id !== user?.id).map((m) => (
                                    <option key={m.id} value={m.id}>
                                      {m.name || m.email} ({m.email})
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <button
                                type="button"
                                className="btn"
                                disabled={!transferTargetId || transferring}
                                onClick={() => handleTransferOwnership(transferTargetId)}
                                style={{
                                  height: "40px",
                                  padding: "0 16px",
                                  boxSizing: "border-box",
                                  background: "var(--accent)",
                                  color: "#fff",
                                  opacity: (!transferTargetId || transferring) ? 0.6 : 1
                                }}
                              >
                                {transferring ? "Transferring..." : "Transfer Ownership"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                        Only workspace owners can delete this workspace or manage its settings.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <h2 className="page-subtitle" style={{ marginTop: 0 }}>Workspace Members</h2>
              
              {!activeWorkspace ? (
                <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>
                  Please select a workspace from the top navigation bar to manage its members.
                </p>
              ) : (
                <div>
                  <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: 12 }}>
                    Manage who has access to this workspace.
                  </p>
 
                  {loadingMembers ? (
                    <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>Loading members...</p>
                  ) : (
                    <div className="members-section">
                      <table className="table" style={{ width: "100%", marginBottom: 20 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: "left" }}>Name</th>
                            <th style={{ textAlign: "left" }}>Email</th>
                            <th style={{ textAlign: "left" }}>Role</th>
                            <th style={{ textAlign: "right" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {members.map((m) => {
                            const isCurrentUser = m.id === user?.id;
                            const isOwnerRole = m.role === "owner";
                            const canRemove = isOwner && !isOwnerRole && !isCurrentUser;
                            
                            return (
                              <tr key={m.id}>
                                <td>
                                  <strong>{m.name || "Pending User"}</strong>
                                  {isCurrentUser && <span style={{ marginLeft: 6, fontSize: "0.75rem", color: "var(--accent)", background: "var(--accent-subtle)", padding: "2px 6px", borderRadius: 4 }}>You</span>}
                                </td>
                                <td>{m.email}</td>
                                <td style={{ textTransform: "capitalize" }}>
                                  {isOwner && !isCurrentUser ? (
                                    <select
                                      className="input"
                                      value={m.role}
                                      onChange={async (e) => {
                                        const nextRole = e.target.value;
                                        try {
                                          await apiPut(`/api/workspaces/${activeWorkspace.id}/members/${m.id}`, { role: nextRole });
                                          showSuccess("Member role updated successfully.");
                                          setMembers((prev) => prev.map((member) => member.id === m.id ? { ...member, role: nextRole } : member));
                                          if (nextRole === "owner") {
                                            await refreshUser();
                                            window.location.reload();
                                          }
                                        } catch (err) {
                                          showError(parseApiError(err, "Failed to update member role"));
                                        }
                                      }}
                                      style={{ padding: "4px 8px", minHeight: "unset", width: "auto" }}
                                    >
                                      <option value="member">Member</option>
                                      <option value="owner">Owner</option>
                                    </select>
                                  ) : (
                                    m.role
                                  )}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  {canRemove && (
                                    <button
                                      type="button"
                                      className="btn btn-ghost btn-sm"
                                      onClick={() => handleRemoveMember(m.id)}
                                      style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
                                    >
                                      Remove
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {isOwner && (
                        <div style={{ background: "#f8fafc", padding: 16, border: "1px solid var(--border)", borderRadius: "var(--radius-sm)" }}>
                          <h4 style={{ margin: "0 0 10px 0", fontSize: "0.9rem" }}>Invite Member</h4>
                          <form onSubmit={handleAddMember} style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <input
                              className="input"
                              type="email"
                              placeholder="member@email.com"
                              value={newMemberEmail}
                              onChange={(e) => setNewMemberEmail(e.target.value)}
                              style={{ flex: 1, minWidth: 200 }}
                              required
                            />
                            <select
                              className="input"
                              value={newMemberRole}
                              onChange={(e) => setNewMemberRole(e.target.value)}
                              style={{ minWidth: 120 }}
                            >
                              <option value="member">Member</option>
                              <option value="owner">Owner</option>
                            </select>
                            <button type="submit" className="btn btn-primary" disabled={addingMember}>
                              {addingMember ? "Inviting..." : "Invite"}
                            </button>
                          </form>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {showChangeEmail ? (
        <ChangeEmailModal
          currentEmail={resolvedProfile?.email || email || ""}
          pendingEmail={resolvedProfile?.pending_email || null}
          onClose={() => setShowChangeEmail(false)}
          onSuccess={handleEmailChanged}
        />
      ) : null}

      {showDeleteModal ? (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(15, 23, 42, 0.6)",
          backdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          padding: "20px"
        }}>
          <div className="auth-card card" style={{ maxWidth: "440px", width: "100%", textAlign: "center", position: "relative" }}>
            <h2 className="page-subtitle" style={{ marginTop: 0, color: "var(--danger)", fontWeight: 700 }}>Delete Account</h2>
            <p style={{ fontSize: "0.88rem", color: "var(--text-muted)", marginBottom: 20, lineHeight: 1.5 }}>
              This action is permanent and cannot be undone. All your project data, schedules, and reports will be deleted immediately.
            </p>
            
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              background: "rgba(239, 68, 68, 0.04)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              marginBottom: "16px",
              gap: "8px"
            }}>
              <span style={{ fontWeight: 600, fontSize: "0.88rem", color: "var(--text-heading)", wordBreak: "break-all", textAlign: "left", flex: 1 }}>
                {resolvedProfile?.email || email}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  navigator.clipboard.writeText(resolvedProfile?.email || email);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                style={{ padding: "4px 10px", fontSize: "0.75rem", minHeight: "unset" }}
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>

            <div style={{ textAlign: "left", marginBottom: "24px" }}>
              <label className="field-label" style={{ display: "block", marginBottom: "6px", fontSize: "0.85rem", fontWeight: 600, color: "var(--text-heading)" }}>
                Confirm your email address
              </label>
              <input
                type="text"
                className="input"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder="Type your email address to confirm"
                style={{ width: "100%", boxSizing: "border-box" }}
              />
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setShowDeleteModal(false);
                  setConfirmEmail("");
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                onClick={handleDeleteAccount}
                disabled={isDeleting || confirmEmail.trim().toLowerCase() !== (resolvedProfile?.email || email || "").toLowerCase()}
                style={{
                  background: "var(--danger)",
                  color: "#fff",
                  opacity: (isDeleting || confirmEmail.trim().toLowerCase() !== (resolvedProfile?.email || email || "").toLowerCase()) ? 0.6 : 1
                }}
              >
                {isDeleting ? "Deleting..." : "Permanently Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Workspace Deletion Modal */}
      {showDeleteWsModal ? (
        <div style={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(15, 23, 42, 0.6)",
          backdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: "20px"
        }}>
          <div className="auth-card card" style={{ maxWidth: "440px", width: "100%", textAlign: "center", position: "relative" }}>
            <h2 className="page-subtitle" style={{ marginTop: 0, color: "var(--danger)", fontWeight: 700 }}>Delete Workspace</h2>
            <p style={{ fontSize: "0.88rem", color: "var(--text-muted)", marginBottom: 20, lineHeight: 1.5 }}>
              This action is permanent. All projects, schedules, crawling logs, and audit reports inside the workspace <strong>{activeWorkspace?.name}</strong> will be permanently deleted.
            </p>
            
            <div style={{ textAlign: "left", marginBottom: "24px" }}>
              <label className="field-label" style={{ display: "block", marginBottom: "6px", fontSize: "0.85rem", fontWeight: 600, color: "var(--text-heading)" }}>
                To confirm, type the workspace name: <strong style={{ color: "var(--text-heading)" }}>{activeWorkspace?.name}</strong>
              </label>
              <input
                type="text"
                className="input"
                value={confirmWsName}
                onChange={(e) => setConfirmWsName(e.target.value)}
                placeholder="Type the workspace name to confirm"
                style={{ width: "100%", boxSizing: "border-box" }}
              />
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setShowDeleteWsModal(false);
                  setConfirmWsName("");
                }}
                disabled={deletingWs}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                onClick={handleDeleteWorkspace}
                disabled={deletingWs || confirmWsName.trim() !== activeWorkspace?.name}
                style={{
                  background: "var(--danger)",
                  color: "#fff",
                  opacity: (deletingWs || confirmWsName.trim() !== activeWorkspace?.name) ? 0.6 : 1
                }}
              >
                {deletingWs ? "Deleting..." : "Permanently Delete Workspace"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
